import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AtSign, CornerDownRight, Flag, ThumbsUp, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Comment,
  CommentPage,
  MentionCandidate,
  MentionCandidateList,
} from '@up2d8/contracts';
import { Avatar, Button, Dialog, SearchInput, Textarea, cn, useToast } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { uiLocale } from '../../shared/format';
import { useCommentPreviews } from './app-interaction';

/** Zoveel reacties per keer; "meer laden" en geen oneindig scrollen, net als bij de feed. */
const PAGE_SIZE = 20;

/**
 * De reacties onder een bericht (Stap 12b).
 *
 * Plat, oudste eerst, en met het invoerveld onderaan — een gesprek leest chronologisch, en wie
 * antwoordt, antwoordt op het laatste. Geen threading: een gesprek van drie niveaus diep is op een
 * telefoon in een fabriek onleesbaar.
 *
 * Sinds Stap 26 zitten er drie dingen bij: een **@-kiezer** die alleen namen voorstelt die het
 * bericht ook kregen, een **meldknop** per reactie, en een regel als reageren **uitstaat** — de
 * bestaande reacties blijven dan staan, er komt alleen niets bij.
 *
 * Wat hier bewust níet gebeurt: offline reageren. Een bevestiging hoort in de wachtrij omdat ze
 * bewijs is en niet verloren mag gaan; een vraag mag gewoon falen met "geen verbinding". Een
 * reactie die drie dagen later vanuit een wachtrij opduikt onder een gesprek dat al verder is, is
 * erger dan een reactie die niet verstuurd werd.
 */
export function CommentSection({ postId }: { postId: string }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState('');
  /** Op welke reactie wordt geantwoord; `null` is "gewoon een nieuwe reactie" (SCH-1). */
  const [replyTo, setReplyTo] = useState<Comment | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Comment | null>(null);
  const [pendingReport, setPendingReport] = useState<Comment | null>(null);
  const [reportReason, setReportReason] = useState('');
  const [mentions, setMentions] = useState<MentionCandidate[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionQuery, setMentionQuery] = useState('');

  // Niet op elke toetsaanslag zoeken: op een telefoon met een trage verbinding blijft de lijst
  // anders onder je vingers verspringen. Zelfde kwart seconde als bij de collega-lijst.
  useEffect(() => {
    const timer = setTimeout(() => setMentionQuery(mentionSearch), 250);
    return () => clearTimeout(timer);
  }, [mentionSearch]);

  // Staan reacties aan? Geen rij op de server betekent aan, dus een mislukte aanroep valt terug
  // op "aan": een leesbare reactielijst met een invoerveld dat misschien een 409 geeft, is beter
  // dan een scherm dat beweert dat reageren uit staat terwijl dat niet zo is.
  // Uit de reactiestrook van de feed (`feed.read`) en niet uit `/posts/{id}/comment-setting`: die
  // hangt aan `communication.post.draft`, dus een medewerker kreeg altijd 403 en zag altijd "aan".
  const previews = useCommentPreviews([postId]);
  const enabled = previews.data?.find((group) => group.postId === postId)?.commentsEnabled ?? true;

  /**
   * De namen die je kunt vermelden, gefilterd door de server.
   *
   * De zoekterm gaat **mee naar de server** en wordt niet in de client gefilterd. Dat is geen
   * optimalisatie: het antwoord is afgekapt op vijfentwintig namen, dus filteren in de client zou
   * zoeken binnen een willekeurige greep uit het alfabet - in een bedrijf van driehonderd mensen
   * vind je dan niemand voorbij de A.
   */
  const candidates = useQuery({
    queryKey: ['mention-candidates', postId, mentionQuery],
    enabled: pickerOpen,
    queryFn: async (): Promise<MentionCandidate[]> => {
      const { data, error } = await api.GET('/api/v1/feed/{postId}/mention-candidates', {
        params: { path: { postId }, query: mentionQuery ? { q: mentionQuery } : {} },
      });
      if (error || !data) return [];
      return (data as MentionCandidateList).items;
    },
  });

  // Per pagina met de cursor van de server. Eerder groeide `limit` met twintig per klik, en voorbij
  // het maximum van vijftig gaf de server 400 en verdwenen alle reacties.
  const comments = useInfiniteQuery({
    queryKey: ['comments', postId],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<CommentPage> => {
      const { data, error } = await api.GET('/api/v1/feed/{postId}/comments', {
        params: {
          path: { postId },
          query: pageParam ? { limit: PAGE_SIZE, cursor: pageParam } : { limit: PAGE_SIZE },
        },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as CommentPage;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  /**
   * "Leuk" op een reactie, of weer weg (SCH-1).
   *
   * Een schakelaar met `PUT`/`DELETE`, net als de like op een bericht. Geen optimistische update:
   * het aantal komt uit rijen, en een teller die even iets anders zegt dan de server is op dit
   * scherm niets waard.
   */
  const likeComment = useMutation({
    mutationFn: async ({ comment }: { comment: Comment }) => {
      const params = { path: { postId, commentId: comment.id } };
      const { error } = comment.likedByMe
        ? await api.DELETE('/api/v1/feed/{postId}/comments/{commentId}/like', { params })
        : await api.PUT('/api/v1/feed/{postId}/comments/{commentId}/like', { params });
      if (error) throw error;
    },
    onSuccess: () => refresh(),
    onError: () => toast.show({ tone: 'danger', title: t('interaction.likeFailed') }),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['comments', postId] });
    // De tellers op de kaart staan onder `comment-counts` en `comment-previews`, met een lijst ids
    // als tweede sleutel; daarom op het voorvoegsel.
    void queryClient.invalidateQueries({ queryKey: ['comment-counts'] });
    void queryClient.invalidateQueries({ queryKey: ['comment-previews'] });
  };

  const add = useMutation({
    mutationFn: async (body: string) => {
      // `parentId` maakt er een antwoord van. Is de ouder zelf een antwoord, dan hangt de server
      // het aan dezelfde ouder — één niveau diep, afgedwongen in de database (SCH-1).
      const { error } = await api.POST('/api/v1/feed/{postId}/comments', {
        params: { path: { postId } },
        body: {
          body,
          mentions: mentions.map((candidate) => candidate.personId),
          parentId: replyTo?.id ?? null,
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setDraft('');
      setReplyTo(null);
      setMentions([]);
      refresh();
    },
    onError: () => {
      toast.show({ tone: 'danger', title: t('interaction.commentFailed') });
    },
  });

  const remove = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await api.DELETE('/api/v1/feed/{postId}/comments/{commentId}', {
        params: { path: { postId, commentId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setPendingDelete(null);
      refresh();
    },
    onError: () => {
      setPendingDelete(null);
      toast.show({ tone: 'danger', title: t('interaction.deleteFailed') });
    },
  });

  const report = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await api.POST('/api/v1/feed/comments/{commentId}/report', {
        params: { path: { commentId } },
        body: { reason: reportReason.trim() },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setPendingReport(null);
      setReportReason('');
      refresh();
      // Geen aantal en geen "we gaan ernaar kijken": de melder hoort te weten dat het aankwam en
      // niets meer. Wat er daarna gebeurt, is moderatiewerk dat hij niet hoort te volgen.
      toast.show({ tone: 'success', title: t('interaction.reported') });
    },
    onError: () => {
      setPendingReport(null);
      toast.show({ tone: 'danger', title: t('interaction.reportFailed') });
    },
  });

  const items = comments.data?.pages.flatMap((page) => page.items) ?? [];

  /*
    Antwoorden staan onder hun ouder (SCH-1).

    De server levert één platte lijst op volgorde van tijd; hier wordt ze gegroepeerd. Dat gebeurt
    in de client en niet in de query, omdat de paginering over de **hele** lijst gaat: zou de
    server per ouder groeperen, dan zou een pagina van twintig soms vijf reacties bevatten en soms
    twintig, en kon je niet meer zeggen hoeveel er nog komen.

    Een antwoord waarvan de ouder nog niet geladen is, valt niet weg maar komt onderaan als gewone
    regel te staan: een reactie die verdwijnt omdat ze op de verkeerde pagina staat, is erger dan
    een reactie die even los staat.
  */
  const parents = items.filter((comment) => comment.parentId === null);
  const repliesByParent = new Map<string, typeof items>();
  for (const comment of items) {
    if (!comment.parentId) continue;
    const bestaand = repliesByParent.get(comment.parentId) ?? [];
    repliesByParent.set(comment.parentId, [...bestaand, comment]);
  }
  const wezen = items.filter(
    (comment) => comment.parentId !== null && !parents.some((p) => p.id === comment.parentId),
  );
  const zichtbaar = [...parents, ...wezen];
  const trimmed = draft.trim();

  return (
    <section
      data-testid="comment-section"
      className="mt-section flex flex-col gap-element border-t border-border-light pt-element"
    >
      <h2 className="font-heading text-title-sm font-semibold text-text-primary">
        {t('interaction.commentsHeading')}
      </h2>

      {items.length === 0 ? (
        <p className="text-content text-text-secondary">{t('interaction.noComments')}</p>
      ) : (
        <ul className="flex flex-col gap-element">
          {zichtbaar.map((comment) => (
            <li key={comment.id}>
              <CommentRow
                comment={comment}
                onLike={() => likeComment.mutate({ comment })}
                onReply={() => setReplyTo(comment)}
                onDelete={() => setPendingDelete(comment)}
                onReport={() => setPendingReport(comment)}
                deleteLabel={t('interaction.deleteComment')}
                reportLabel={t('interaction.report')}
                likeLabel={t('interaction.like')}
                replyLabel={t('interaction.reply')}
                deletedLabel={t('interaction.commentDeleted')}
              />

              {/* Antwoorden staan ingesprongen onder hun ouder (SCH-2). De inspringing is de
                  breedte van de avatar plus de tussenruimte: zo lijnt een antwoord uit met de
                  tekst van de vraag erboven, en niet met de rand. */}
              {(repliesByParent.get(comment.id) ?? []).length > 0 && (
                <ul className="mt-2 flex flex-col gap-2 border-l border-border-light pl-4">
                  {(repliesByParent.get(comment.id) ?? []).map((reply) => (
                    <li key={reply.id}>
                      <CommentRow
                        comment={reply}
                        onLike={() => likeComment.mutate({ comment: reply })}
                        // Antwoorden op een antwoord hangen aan dezelfde ouder; de knop wijst
                        // daarom naar de ouder en niet naar het antwoord.
                        onReply={() => setReplyTo(comment)}
                        onDelete={() => setPendingDelete(reply)}
                        onReport={() => setPendingReport(reply)}
                        deleteLabel={t('interaction.deleteComment')}
                        reportLabel={t('interaction.report')}
                        likeLabel={t('interaction.like')}
                        replyLabel={t('interaction.reply')}
                        deletedLabel={t('interaction.commentDeleted')}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      {comments.hasNextPage && (
        <Button
          variant="secondary"
          disabled={comments.isFetchingNextPage}
          onClick={() => void comments.fetchNextPage()}
        >
          {t('interaction.moreComments')}
        </Button>
      )}

      {enabled ? (
        <form
          className="flex flex-col gap-element"
          onSubmit={(event) => {
            event.preventDefault();
            if (trimmed.length > 0) add.mutate(trimmed);
          }}
        >
          {/* Op wie je antwoordt, met een weg eruit. Zonder deze regel weet niemand waar zijn
              tekst terechtkomt zodra hij een eindje gescrold heeft. */}
          {replyTo && (
            <p
              data-testid="reply-to"
              className="flex items-center justify-between gap-2 rounded-md bg-surface-sunk px-3 py-2 text-small text-text-secondary"
            >
              <span className="truncate">
                {t('interaction.replyingTo', { name: replyTo.authorName })}
              </span>
              <button
                type="button"
                data-touch-target
                data-testid="cancel-reply"
                onClick={() => setReplyTo(null)}
                className="shrink-0 rounded-md px-2 py-1 text-text-muted hover:text-brand-primary"
              >
                {t('common.cancel')}
              </button>
            </p>
          )}
          <Textarea
            id="comment-body"
            label={t('interaction.commentLabel')}
            placeholder={t('interaction.commentPlaceholder')}
            value={draft}
            maxLength={2000}
            onChange={(event) => setDraft(event.target.value)}
          />

          {/* De @-kiezer is een knop met een lijst en geen typeaanvulling in het tekstveld. Op een
              telefoon met werkhandschoenen is "typ @ en dan drie letters" een reeks die vaak
              mislukt; een lijst aantikken niet. En de server krijgt ids in plaats van namen, dus
              drie Jannen in een bedrijf leveren geen verkeerde vermelding op. */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-touch-target
              data-testid="mention-open"
              onClick={() => setPickerOpen((current) => !current)}
              className="inline-flex min-h-touch items-center gap-1.5 rounded-md border border-border-light px-2 py-1 text-small text-text-secondary hover:border-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              <AtSign aria-hidden className="h-4 w-4" />
              {t('interaction.mention')}
            </button>
            {mentions.map((candidate) => (
              <button
                type="button"
                key={candidate.personId}
                data-testid="mention-chip"
                onClick={() =>
                  setMentions((current) =>
                    current.filter((entry) => entry.personId !== candidate.personId),
                  )
                }
                className="inline-flex min-h-touch items-center gap-1 rounded-pill bg-brand-primary px-3 py-1 text-small text-text-on-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
              >
                {candidate.displayName}
                <span aria-hidden>×</span>
                <span className="sr-only">{t('interaction.removeMention')}</span>
              </button>
            ))}
          </div>

          {pickerOpen && (
            <SearchInput
              label={t('interaction.mentionSearch')}
              placeholder={t('interaction.mentionSearchPlaceholder')}
              value={mentionSearch}
              data-testid="mention-search"
              onChange={(event) => setMentionSearch(event.target.value)}
            />
          )}

          {pickerOpen && (
            <ul
              data-testid="mention-list"
              className="max-h-48 overflow-y-auto rounded-md border border-border-light bg-surface"
            >
              {(candidates.data ?? []).length === 0 ? (
                <li className="px-3 py-2 text-small text-text-muted">
                  {t('interaction.noMentionCandidates')}
                </li>
              ) : (
                (candidates.data ?? [])
                  .filter(
                    (candidate) => !mentions.some((entry) => entry.personId === candidate.personId),
                  )
                  .map((candidate) => (
                    <li key={candidate.personId}>
                      <button
                        type="button"
                        data-touch-target
                        data-testid="mention-candidate"
                        onClick={() => {
                          setMentions((current) => [...current, candidate]);
                          setPickerOpen(false);
                        }}
                        className="flex min-h-touch w-full items-center px-3 py-2 text-left text-content text-text-primary hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                      >
                        {candidate.displayName}
                      </button>
                    </li>
                  ))
              )}
            </ul>
          )}

          <Button type="submit" loading={add.isPending} disabled={trimmed.length === 0}>
            {t('interaction.send')}
          </Button>
        </form>
      ) : (
        // De bestaande reacties blijven staan, er komt alleen niets bij. Een discussie die
        // verdwijnt op het moment dat iemand hem sluit, leest als iets wat weggemoffeld wordt.
        <p data-testid="comments-closed" className="text-content text-text-muted">
          {t('interaction.closed')}
        </p>
      )}

      {/* Een bevestigingsvraag bij het weghalen: de reactie is zacht verwijderd en dus niet
          werkelijk weg, maar voor de medewerker is ze dat wel — en een tik naast de knop op een
          telefoon in werkhandschoenen gebeurt vaker dan je denkt. */}
      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t('interaction.deleteTitle')}
        description={t('interaction.deleteConfirm')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingDelete(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="urgent"
              loading={remove.isPending}
              data-testid="confirm-delete-comment"
              onClick={() => pendingDelete && remove.mutate(pendingDelete.id)}
            >
              {t('interaction.deleteComment')}
            </Button>
          </>
        }
      />

      {/* Melden vraagt om een reden, maar hij mag leeg blijven: wie iets ergs ziet, moet het
          kunnen doorgeven zonder eerst een formulier te schrijven. */}
      <Dialog
        open={pendingReport !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingReport(null);
            setReportReason('');
          }
        }}
        title={t('interaction.reportTitle')}
        description={t('interaction.reportHint')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingReport(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={report.isPending}
              data-testid="confirm-report-comment"
              onClick={() => pendingReport && report.mutate(pendingReport.id)}
            >
              {t('interaction.report')}
            </Button>
          </>
        }
      >
        <Textarea
          id="report-reason"
          label={t('interaction.reportReason')}
          value={reportReason}
          maxLength={500}
          onChange={(event) => setReportReason(event.target.value)}
        />
      </Dialog>
    </section>
  );
}

/**
 * Eén reactie of antwoord (bericht-detail-spec §4.7).
 *
 * Dezelfde rij op beide plaatsen: een reactie en een antwoord zien er hetzelfde uit, alleen staat
 * het antwoord ingesprongen. Twee componenten zouden betekenen dat een wijziging aan de ene de
 * andere stil ongemoeid laat.
 *
 * Een zacht verwijderde reactie die er nog staat omdat er antwoorden onder hangen, toont geen tekst
 * en geen acties — alleen dat ze weg is. Zonder die regel staat er een lege ballon met een
 * duim-knop eronder.
 */
function CommentRow({
  comment,
  onLike,
  onReply,
  onDelete,
  onReport,
  deleteLabel,
  reportLabel,
  likeLabel,
  replyLabel,
  deletedLabel,
}: {
  comment: Comment;
  onLike: () => void;
  onReply: () => void;
  onDelete: () => void;
  onReport: () => void;
  deleteLabel: string;
  reportLabel: string;
  likeLabel: string;
  replyLabel: string;
  deletedLabel: string;
}) {
  const tijd = new Date(comment.createdAt).toLocaleString(uiLocale(), {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  if (comment.deleted) {
    return (
      <p
        data-testid="comment"
        data-deleted="true"
        className="rounded-lg bg-surface-sunk px-3 py-2 text-small italic text-text-muted"
      >
        {deletedLabel}
      </p>
    );
  }

  return (
    <div data-testid="comment" className="flex gap-2.5 rounded-lg bg-surface-sunk px-3 py-2">
      <Avatar name={comment.authorName} size="md" className="h-9 w-9" />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-element">
          <span className="font-heading text-small font-semibold text-text-primary">
            {comment.authorName}
          </span>
          <span className="shrink-0 text-caption text-text-muted">{tijd}</span>
        </div>

        {/* Platte tekst, dus `whitespace-pre-wrap` en geen HTML: wat een medewerker typt, wordt
            nooit opmaak. De berichttekst van een opsteller gaat door een witte lijst (ADR-0037);
            een reactie krijgt die weg niet. */}
        <p className="mt-0.5 whitespace-pre-wrap break-words text-content text-text-primary">
          {comment.body}
        </p>

        {/* De vermeldingen als aparte regel en niet gemarkeerd in de tekst zelf. Zoeken naar "@Jan"
            in platte tekst zou een reactie met "mail naar jan@..." half gemarkeerd maken. */}
        {comment.mentions.length > 0 && (
          <p className="mt-1 flex flex-wrap items-center gap-1 text-small text-brand-primary">
            <AtSign aria-hidden className="h-3.5 w-3.5 shrink-0" />
            {comment.mentions.map((mention) => mention.displayName).join(', ')}
          </p>
        )}

        <div className="mt-1 flex flex-wrap items-center gap-1">
          <button
            type="button"
            data-touch-target
            data-testid="like-comment"
            aria-pressed={comment.likedByMe}
            onClick={onLike}
            className={cn(
              'inline-flex min-h-touch items-center gap-1.5 rounded-md px-2 py-1 text-small hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary',
              comment.likedByMe ? 'text-brand-primary' : 'text-text-muted',
            )}
          >
            <ThumbsUp
              aria-hidden
              className="h-4 w-4"
              fill={comment.likedByMe ? 'currentColor' : 'none'}
            />
            {/* Het aantal staat er alleen als er een is: "Leuk · 0" leest als een verwijt. */}
            {comment.likeCount > 0 ? `${likeLabel} \u00b7 ${comment.likeCount}` : likeLabel}
          </button>

          <button
            type="button"
            data-touch-target
            data-testid="reply-comment"
            onClick={onReply}
            className="inline-flex min-h-touch items-center gap-1.5 rounded-md px-2 py-1 text-small text-text-muted hover:bg-surface hover:text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            <CornerDownRight aria-hidden className="h-4 w-4" />
            {replyLabel}
          </button>

          {comment.canDelete && (
            <button
              type="button"
              data-touch-target
              data-testid="delete-comment"
              onClick={onDelete}
              className="inline-flex min-h-touch items-center gap-1.5 rounded-md px-2 py-1 text-small text-text-muted hover:bg-surface hover:text-danger focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              <Trash2 aria-hidden className="h-4 w-4" />
              {deleteLabel}
            </button>
          )}

          {!comment.reportedByMe && (
            <button
              type="button"
              data-touch-target
              data-testid="report-comment"
              onClick={onReport}
              className="inline-flex min-h-touch items-center gap-1.5 rounded-md px-2 py-1 text-small text-text-muted hover:bg-surface hover:text-text-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              <Flag aria-hidden className="h-4 w-4" />
              {reportLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
